import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, Grid, Paper, TextField, Typography } from '@mui/material';
import type { Contact, Passenger } from '../../../api/types/common';
import { PhoneField } from '../../../components/PhoneField';

const driverSchema = z.object({
  firstName: z.string().min(1, 'Ad gerekli'),
  lastName: z.string().min(1, 'Soyad gerekli'),
  birthDate: z.string().min(1, 'Doğum tarihi gerekli'),
  citizenNo: z.string().optional(),
  phone: z.string().min(1, 'Telefon gerekli'),
  email: z.string().min(1, 'E-posta gerekli').email('Geçerli bir e-posta girin'),
});

export type DriverFormValues = z.infer<typeof driverSchema>;

interface DriverFormStepProps {
  loading?: boolean;
  error?: string | null;
  onSubmit: (driver: { passenger: Passenger; contact: Contact }) => void;
}

export function DriverFormStep({ loading, error, onSubmit }: DriverFormStepProps) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DriverFormValues>({
    resolver: zodResolver(driverSchema),
    defaultValues: { firstName: '', lastName: '', birthDate: '', citizenNo: '', phone: '', email: '' },
  });

  const submit = (values: DriverFormValues) => {
    const passenger: Passenger = {
      firstName: values.firstName,
      lastName: values.lastName,
      birthDate: values.birthDate,
      citizenNo: values.citizenNo || undefined,
      phone: values.phone,
      email: values.email,
      paxType: 'Adult',
    };
    const contact: Contact = {
      name: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values.email,
    };
    onSubmit({ passenger, contact });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Sürücü Bilgileri
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box component="form" onSubmit={handleSubmit(submit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Ad"
              size="small"
              fullWidth
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              {...register('firstName')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Soyad"
              size="small"
              fullWidth
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              {...register('lastName')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Doğum Tarihi"
              size="small"
              fullWidth
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              error={!!errors.birthDate}
              helperText={errors.birthDate?.message}
              {...register('birthDate')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="TC Kimlik / Kimlik No"
              size="small"
              fullWidth
              error={!!errors.citizenNo}
              helperText={errors.citizenNo?.message}
              {...register('citizenNo')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <PhoneField
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  label="Telefon"
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="E-posta"
              size="small"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email')}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Kaydediliyor…' : 'Devam Et'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
